import React, { useCallback, useContext, useEffect, useState } from "react";

import { Edit } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import {
  Alert,
  Box,
  CircularProgress,
  FormLabel,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { SubscriptionComponent } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import {
  ChargePolicy,
  ProrationPolicy,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { ChargifyState } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { toUUID } from "../../../../helpers/s12-helpers";
import EnumRadio from "./enum-radio";
import {
  getSubComponentWithDefaultPriceBySubId,
  updateSubscriptionComponent as updateSubscriptionComponentApi,
} from "api/endpoints/components";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import CurrencyFormatter from "components/shared/currency-formatter";
import {
  chargePolicyDisplayMapping,
  downgradeProrationPolicyDisplayMapping,
  upgradeProrationPolicyDisplayMapping,
} from "constants/component";
import {
  DEFAULT_SNACKBAR_DURATION,
  currencyMapping,
  pricingPeriodToMonthsMapping,
} from "constants/index";
import {
  MIN_FULL_SEATS,
  MIN_LITE_SEATS,
  PREMIUM_SEATS_MAX_SEATS_DEFAULT,
  PREMIUM_SEATS_MAX_SEATS_UPPER_DEFAULT,
  userSeatsToString,
} from "constants/seats";
import { SubscriptionSeatUsageContext } from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import proration from "helpers/proration";
import {
  getBillingFrequency,
  getBillingProviderName,
  getSubscriptionCurrency,
} from "helpers/subscriptions";
import {
  ChangeComponentQuantityWrapperProps,
  UrlParamTypes,
} from "types/org-display";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40rem",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};

const ChangeComponentQuantity = ({
  subscription,
  refreshOrgData,
}: ChangeComponentQuantityWrapperProps) => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const subId = subscription?.id;
  const state = subscription?.chargify?.state || 0;

  const { subscriptionSeatUsage } = useContext(SubscriptionSeatUsageContext);
  const { hasGroups, hasPermissions, permissions } =
    useContext(PermissionsContext);
  const canEditQuantity =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_SUBSCRIPTION_COMPONENT_QUANTITY]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showProcessingPage, setShowProcessingPage] = useState(false);

  const currency = getSubscriptionCurrency(subscription);
  const billingFrequency = getBillingFrequency(subscription);
  const billingProviderName = getBillingProviderName(subscription);

  // temporary until we reach phase 3 of sensor units
  // https://safetyculture.atlassian.net/browse/BSA-10201
  const canAllocateSensorPlanUnit = hasGroups([
    "App - Business Tools - AR",
    "App - Business Tools - BSA",
    "Business Tools Preview - Engineer", // non-production okta group
  ]);

  const canAllocatePremiumComponentUpperBound = hasGroups([
    "App - Business Tools - AR",
    "App - Business Tools - BSA",
    "Business Tools Preview - Engineer", // non-production okta group
  ]);

  const MAX_SEATS = canAllocatePremiumComponentUpperBound
    ? PREMIUM_SEATS_MAX_SEATS_UPPER_DEFAULT
    : PREMIUM_SEATS_MAX_SEATS_DEFAULT;

  const [
    selectedComponentNewTotalQuantity,
    setSelectedComponentNewTotalQuantity,
  ] = useState<number>(0);
  const [
    selectedComponentAssignedQuantity,
    setSelectedComponentAssignedQuantity,
  ] = useState<number>(0);
  const [selectedComponent, setSelectedComponent] =
    useState<SubscriptionComponent>();
  const [selectedComponentID, setSelectedComponentID] = useState<number>(0);
  const [selectedComponentQuantity, setSelectedComponentQuantity] =
    useState<number>(0);
  const [components, setComponents] = useState<SubscriptionComponent[]>([]);
  const [selectedComponentLabel, setSelectedComponentLabel] =
    useState<string>("");
  const [selectedMinSeat, setSelectedMinSeat] = useState<number>(0);
  const [, setOpenConfirmDialog] = useState(false);
  const [memo, setMemo] = useState<string>(
    "Update subscription user seats quota",
  );
  const [upgradePorationPolicy, setUpgradePorationPolicy] =
    useState<ProrationPolicy>(proration.GetProrationPolicy(state));
  const [downgradePorationPolicy, setDowngradePorationPolicy] =
    useState<ProrationPolicy>(proration.GetProrationPolicy(state));
  const [chargePolicy, setChargePolicy] = useState<ChargePolicy>(
    ChargePolicy.IMMEDIATE,
  );
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const { showError } = useContext(SnackbarContext);
  const isFreeOrg =
    subscription &&
    (subscription.type === SubscriptionType.FREE ||
      subscription.type === SubscriptionType.FREE_ORG);
  const subActive = subscription && subscription.active === true;
  const chargifyStateUnknownOrCanceled =
    subscription &&
    subscription.chargify &&
    [ChargifyState.CANCELED, ChargifyState.UNSPECIFIED].includes(
      subscription.chargify.state,
    );

  const getSubscriptionComponents = useCallback(() => {
    if (subId == undefined || isFreeOrg || currency == undefined) {
      return;
    }

    setShowProcessingPage(true);

    getSubComponentWithDefaultPriceBySubId(
      {
        subId: toUUID(subId),
        currency: currency,
        period: pricingPeriodToMonthsMapping[billingFrequency],
        site: subscription?.chargify?.site,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((res) => {
        setComponents(res);
        res.forEach((c) => {
          if (c.id === selectedComponentID) {
            setSelectedComponent(c);
            setSelectedComponentQuantity(c.quantity);
            setSelectedComponentNewTotalQuantity(c.quantity);
          }
        });
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
        setShowProcessingPage(false);
      });
  }, [
    subId,
    billingFrequency,
    currency,
    regionCode,
    roleId,
    showError,
    selectedComponentID,
    subscription?.chargify?.site,
    isFreeOrg,
  ]);

  useEffect(() => {
    if (isFreeOrg) {
      return;
    }
    getSubscriptionComponents();
  }, [
    getSubscriptionComponents,
    isFreeOrg,
    selectedComponent?.period,
    selectedComponent?.quantity,
    billingFrequency,
    subscriptionSeatUsage?.premiumSeatUsageSummary?.purchasedSeats,
    showModal,
  ]);

  const updateSubscriptionComponent = async (
    componentPriceId: number,
    newQuantity: number,
    upgradeProrationPolicy: ProrationPolicy,
    downgradeProrationPolicy: ProrationPolicy,
    chargePolicy: ChargePolicy,
    memo: string,
  ): Promise<boolean> => {
    setIsSubscriptionActionLoading(true);
    const updated = await updateSubscriptionComponentApi(
      {
        subId: toUUID(subId),
        componentPricePointId: componentPriceId,
        quantity: newQuantity,
        upgradeProrationPolicy,
        downgradeProrationPolicy,
        chargePolicy,
        memo,
        organizationId: toUUID(roleId),
        componentType: selectedComponent?.componentType || 0,
      },
      callOptionsHeaders(regionCode, roleId),
    ).catch((error) => {
      setShowModal(false);
      showError(error);
      return false;
    });
    setShowProcessingPage(false);
    setIsSubscriptionActionLoading(false);
    if (updated) {
      setShowModal(false);
      setIsLoading(true);
      setShowSuccessSnackbar(true);
      refreshOrgData();
      return new Promise((resolve) => resolve(true));
    } else {
      return new Promise((resolve) => resolve(false));
    }
  };

  const displaySubscriptionComponents = () => {
    if (components && components.length > 0) {
      return components
        .sort((a, b) => a.id - b.id)
        .map((c) => getComponentDisplay(c));
    } else {
      return (
        <>Unable to load seats info from Chargify, please refresh the page.</>
      );
    }
  };

  const getComponentDisplay = (component: SubscriptionComponent) => {
    let occupiedSeats = 0;
    let minSeat = 0;
    const { name, quantity } = component;
    let seatLabel = userSeatsToString[name];
    let unit = "seat";

    if (name.includes("Full")) {
      seatLabel = "Full";
    }
    if (name.includes("Lite")) {
      seatLabel = "Lite";
    }
    if (name.includes("Sensor")) {
      unit = "unit";
      seatLabel = "Sensor";
    }
    if (seatLabel == "Full") {
      occupiedSeats =
        subscriptionSeatUsage?.premiumSeatUsageSummary?.occupiedSeats || 0;
      minSeat = MIN_FULL_SEATS;
    }
    if (seatLabel == "Free") {
      occupiedSeats =
        subscriptionSeatUsage?.collaboratorSeatUsageSummary?.occupiedSeats || 0;
    }
    if (seatLabel == "Lite") {
      occupiedSeats =
        subscriptionSeatUsage?.liteSeatUsageSummary?.occupiedSeats || 0;
      minSeat = MIN_LITE_SEATS;
    }

    if (!seatLabel) {
      return null;
    }

    return (
      <Typography key={name} align="left" variant="body2">
        <b
          data-testid={seatLabel.toLowerCase() + "-" + unit + "-quantity-label"}
        >
          {seatLabel}:
        </b>{" "}
        <label data-testid={seatLabel.toLowerCase() + "-" + unit + "-quantity"}>
          {quantity}
        </label>
        {seatLabel != "Free" &&
        canEditQuantity &&
        (seatLabel != "Sensor" || canAllocateSensorPlanUnit) ? (
          <IconButton
            href=""
            target="_blank"
            rel="noopener"
            size="small"
            color="primary"
            data-testid={
              seatLabel.toLowerCase() + "-" + unit + "-quantity-edit-button"
            }
            style={{
              position: "relative",
              bottom: 2,
              left: 5,
              padding: 1,
            }}
            onClick={() => {
              setShowModal(true);
              setSelectedComponentID(component.id);
              setSelectedComponentAssignedQuantity(occupiedSeats);
              setSelectedComponentLabel(seatLabel);
              setSelectedMinSeat(minSeat);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
        ) : null}
      </Typography>
    );
  };

  const isAllowedToChangeQuantity: boolean =
    selectedComponentNewTotalQuantity >= selectedComponentAssignedQuantity &&
    selectedComponentNewTotalQuantity != selectedComponentQuantity &&
    selectedComponentNewTotalQuantity <= MAX_SEATS &&
    selectedComponentNewTotalQuantity >= selectedMinSeat;

  const renderComponentPrice = () => {
    return (
      <CurrencyFormatter
        priceInCents={selectedComponent?.componentPrice[0].price || 0}
        currency={
          currency
            ? currencyMapping[currency as keyof typeof currencyMapping]
            : 0
        }
      />
    );
  };

  const updateQuantityForm = () => {
    const renderInfoIncreaseSeatsAlert = () => {
      if (
        selectedComponentNewTotalQuantity <= selectedComponentQuantity ||
        !isAllowedToChangeQuantity ||
        state == ChargifyState.TRIALING
      ) {
        return;
      }

      if (upgradePorationPolicy == ProrationPolicy.NONE) {
        return (
          <Alert
            severity="info"
            variant="filled"
            data-testid="info-increase-seats"
            className={clsx(classes.spaceBottomSmall)}
          >
            Changing the seats on this account will incur no cost. Please
            confirm the quantity before proceeding.
          </Alert>
        );
      }

      return (
        <Alert
          severity="info"
          variant="filled"
          data-testid="info-increase-seats"
          className={clsx(classes.spaceBottomSmall)}
        >
          Changing the seats on this account will{" "}
          {upgradeProrationPolicyDisplayMapping[
            upgradePorationPolicy
          ].toLowerCase()}{" "}
          of {renderComponentPrice()} per additional seat{" "}
          <label>
            {chargePolicy == ChargePolicy.DELAY
              ? "at the next renewal date"
              : "immediately"}
          </label>
          . Please confirm the price and quantity before proceeding.
        </Alert>
      );
    };

    const renderInfoTrialIncreaseSeatsAlert = () => {
      if (
        state == ChargifyState.TRIALING &&
        isAllowedToChangeQuantity &&
        selectedComponentNewTotalQuantity > selectedComponentQuantity
      ) {
        return (
          <Alert
            severity="info"
            variant="filled"
            data-testid="info-trial-increase-seats"
            className={clsx(classes.spaceBottomSmall)}
          >
            You are about to add additional{" "}
            {selectedComponentNewTotalQuantity - selectedComponentQuantity}{" "}
            {selectedComponentLabel.toLowerCase()} seats.
          </Alert>
        );
      }
      return null;
    };

    const renderInfoTrialDecreaseSeatsAlert = () => {
      if (
        state == ChargifyState.TRIALING &&
        isAllowedToChangeQuantity &&
        selectedComponentNewTotalQuantity < selectedComponentQuantity
      ) {
        return (
          <Alert
            severity="info"
            variant="filled"
            data-testid="info-trial-decrease-seats"
            className={clsx(classes.spaceBottomSmall)}
          >
            You are about to decrease the {selectedComponentLabel.toLowerCase()}{" "}
            seats in your plan.
          </Alert>
        );
      }
      return null;
    };

    const renderInfoDecreaseSeatsAlert = () => {
      if (
        selectedComponentNewTotalQuantity >= selectedComponentQuantity ||
        !isAllowedToChangeQuantity ||
        state == ChargifyState.TRIALING
      ) {
        return;
      }

      if (downgradePorationPolicy == ProrationPolicy.NONE) {
        return (
          <Alert
            severity="info"
            variant="filled"
            data-testid="info-decrease-seats"
            className={clsx(classes.spaceBottomSmall)}
          >
            Reducing the seats on this account{" "}
            {downgradeProrationPolicyDisplayMapping[
              downgradePorationPolicy
            ].toLowerCase()}{" "}
            back the unused amount. Please confirm before proceeding.
          </Alert>
        );
      }

      return (
        <Alert
          severity="info"
          variant="filled"
          data-testid="info-decrease-seats"
          className={clsx(classes.spaceBottomSmall)}
        >
          Reducing the seats on this account will credit the{" "}
          <b>
            {downgradePorationPolicy == ProrationPolicy.PRORATED
              ? "prorated"
              : "full"}
          </b>{" "}
          amount of {renderComponentPrice()} per difference in quantity
          decreases to the next billing. Please confirm before proceeding.
        </Alert>
      );

      return null;
    };

    const renderErrorMustBeMoreThanAssignedSeatsAlert = () => {
      if (
        selectedComponentNewTotalQuantity < selectedComponentAssignedQuantity
      ) {
        return (
          <Alert
            severity="warning"
            variant="filled"
            data-testid="error-must-be-more-than-assigned-seats"
            className={clsx(classes.spaceBottomSmall)}
          >
            The total seat must be more than the assigned seat.
          </Alert>
        );
      }
      return null;
    };

    const renderErrorExceedMaxSeatsAlert = () => {
      if (selectedComponentNewTotalQuantity > MAX_SEATS) {
        return (
          <Alert
            severity="warning"
            variant="filled"
            data-testid="error-exceed-max-seats"
            className={clsx(classes.spaceBottomSmall)}
          >
            The total seat should not exceed {MAX_SEATS}.
          </Alert>
        );
      }
      return null;
    };

    const handleUpgradeProrationPolicyChange = (value: string) => {
      setUpgradePorationPolicy(Number(value) as ProrationPolicy);
    };

    const handleDowngradeProrationPolicyChange = (value: string) => {
      setDowngradePorationPolicy(Number(value) as ProrationPolicy);
    };

    const handleChargePolicyChange = (value: string) => {
      setChargePolicy(Number(value) as ChargePolicy);
    };

    return (
      <ConfirmDialog
        title="Update Seat Quantity"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        open={showModal}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => {
          setShowProcessingPage(true);
          updateSubscriptionComponent(
            selectedComponent?.componentPrice[0]?.id as number,
            selectedComponentNewTotalQuantity,
            upgradePorationPolicy,
            downgradePorationPolicy,
            chargePolicy,
            memo,
          );
        }}
        onCancel={() => handleModalClose()}
        disabled={isSubscriptionActionLoading || !isAllowedToChangeQuantity}
      >
        <Box sx={{ width: "100%", paddingTop: "0.5rem" }}>
          <FormLabel component="legend">
            <b>{selectedComponentLabel} seats: </b>
            {selectedComponentQuantity} total,{" "}
            {selectedComponentAssignedQuantity} assigned
          </FormLabel>
          <TextField
            fullWidth
            margin="normal"
            label="Enter the new total seats"
            variant={"outlined"}
            type="number"
            onChange={(e) =>
              setSelectedComponentNewTotalQuantity(parseInt(e.target.value))
            }
            value={selectedComponentNewTotalQuantity}
            InputProps={{
              inputProps: {
                min: selectedMinSeat,
                max: MAX_SEATS,
                "data-testid": "enter-new-total-quantity-field",
              },
            }}
          />
        </Box>
        <EnumRadio
          enum={ChargePolicy}
          label="When there is a charge, I want to:"
          defaultValue={chargePolicy.toString()}
          onValueChange={handleChargePolicyChange}
          displayMapping={chargePolicyDisplayMapping}
        />
        <EnumRadio
          enum={ProrationPolicy}
          label="When price of the difference in quantity increases, I want to:"
          defaultValue={upgradePorationPolicy.toString()}
          onValueChange={handleUpgradeProrationPolicyChange}
          displayMapping={upgradeProrationPolicyDisplayMapping}
        />
        <EnumRadio
          enum={ProrationPolicy}
          label="When price of the difference in quantity decreases, I want to:"
          defaultValue={downgradePorationPolicy.toString()}
          onValueChange={handleDowngradeProrationPolicyChange}
          displayMapping={downgradeProrationPolicyDisplayMapping}
        />
        <Box sx={{ width: "100%", paddingTop: "0.5rem" }}>
          <TextField
            fullWidth
            margin="normal"
            label="Memo"
            variant={"outlined"}
            type="string"
            onChange={(e) => setMemo(e.target.value)}
            value={memo}
            InputProps={{
              inputProps: {
                "data-testid": "enter-memo",
              },
            }}
          />
        </Box>
        <Box sx={{ width: "100%", paddingTop: "0.5rem" }}>
          {renderInfoIncreaseSeatsAlert()}
          {renderInfoTrialIncreaseSeatsAlert()}
          {renderInfoTrialDecreaseSeatsAlert()}
          {renderInfoDecreaseSeatsAlert()}
          {renderErrorMustBeMoreThanAssignedSeatsAlert()}
          {renderErrorExceedMaxSeatsAlert()}
        </Box>
      </ConfirmDialog>
    );
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedComponentNewTotalQuantity(0);
  };

  if (
    !subscription ||
    isFreeOrg ||
    !subActive ||
    chargifyStateUnknownOrCanceled
  ) {
    return null;
  }

  return (
    <>
      {isLoading ? (
        <CircularProgress
          color="primary"
          variant="indeterminate"
          thickness={4}
          size={25}
          data-testid="loading-icon"
        />
      ) : (
        <>{displaySubscriptionComponents()}</>
      )}
      {showModal && (
        <Modal
          open={showModal}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          data-testid="change-component-quantity-modal"
        >
          <Box sx={style}>
            {!showProcessingPage ? (
              <>{updateQuantityForm()}</>
            ) : (
              <>
                <Grid
                  container
                  justifyContent="center"
                  className={clsx(
                    classes.spaceTopSmall,
                    classes.spaceRightMedium,
                  )}
                >
                  <CircularProgress
                    color="primary"
                    variant="indeterminate"
                    thickness={4}
                    size={25}
                    data-testid="loading-icon"
                  />
                </Grid>
              </>
            )}
          </Box>
        </Modal>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={showSuccessSnackbar}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => {
          setShowSuccessSnackbar(false);
        }}
        data-testid="synced-seats-success-toast"
      >
        <Alert
          icon={<CheckIcon fontSize="inherit" />}
          severity="success"
          variant="filled"
        >
          {billingProviderName} Total Seats and SC Total Seats Updated
          Successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChangeComponentQuantity;
